import { t } from 'i18next';
import type { ReactNode } from 'react';
import type { EmptyStateAction, EmptyStateProps, ImageType } from './types';

export type EmptyStateButton = EmptyStateAction & { variant: 'primary' | 'secondary' };

/** What every theme renders, so the kinds pick their art, copy and buttons in one place. */
export type EmptyStateModel = {
  title: string;
  description?: string;
  compact: boolean;
  illustration?: ImageType;
  /** No-results states show a search icon in place of a picture */
  searchIcon: boolean;
  availability?: ReactNode;
  details?: ReactNode;
  actions: EmptyStateButton[];
};

function buttons(
  primary: EmptyStateAction | null | undefined,
  secondary: EmptyStateAction | undefined,
): EmptyStateButton[] {
  return [
    ...(primary ? [{ ...primary, variant: 'primary' as const }] : []),
    ...(secondary ? [{ ...secondary, variant: 'secondary' as const }] : []),
  ];
}

export function emptyStateModel(props: EmptyStateProps): EmptyStateModel {
  const compact = props.compact ?? false;

  switch (props.kind) {
    case 'empty':
    case 'unavailable':
      return {
        title: props.title,
        description: props.description,
        compact,
        illustration: props.illustration,
        searchIcon: false,
        actions: buttons(props.primaryAction, props.secondaryAction),
      };
    case 'no-results':
      return {
        title: props.title,
        description: props.description,
        compact,
        searchIcon: true,
        actions: props.onClearFilters
          ? [
              {
                label: t('tables:filters.clear_all'),
                onClick: props.onClearFilters,
                variant: 'secondary',
              },
            ]
          : [],
      };
    case 'upsell':
      return {
        title: props.title,
        description: props.description,
        compact,
        illustration: props.illustration,
        searchIcon: false,
        availability: props.availability,
        details: props.details,
        actions: buttons(
          { label: t('buttons:upgrade'), icon: 'upgrade', onClick: props.onUpgrade },
          props.secondaryAction,
        ),
      };
    case 'error':
      return {
        title: props.title ?? t('titles:error_page'),
        description: props.description ?? t('errors:generic'),
        compact,
        illustration: 'errorState',
        searchIcon: false,
        actions: props.onRetry
          ? [
              {
                label: t('buttons:reload'),
                icon: 'refresh',
                onClick: props.onRetry,
                variant: 'secondary',
              },
            ]
          : [],
      };
    case 'not-found':
      return {
        title: props.title,
        description: props.description,
        compact,
        illustration: 'errorState',
        searchIcon: false,
        actions: [{ ...props.backAction, variant: 'secondary' }],
      };
  }
}
